import React from 'react';
import { Button, Badge, Tag } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faPhoneAlt, faCoins, faChartLine, faGift } from '@fortawesome/free-solid-svg-icons';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider, DefaultTheme } from 'styled-components';
import { AppDispatch, RootState } from '../../redux/store';
import { toggleCustomerCollapse } from './store/loyaltySlice';
import { CustomerLoyalty, CustomerTableProps } from './types';
import { PHONE_MASK_LENGTH, LOYALTY_CONSTANTS } from './constants';
import TablePagination from '../../commonComponents/Pagination';
import {
  StyledTable,
  TableRow,
  IconBadge,
  TransactionCard,
  TransactionHeader,
  TransactionTable,
  ColumnTitleWrapper,
  UserNameText,
  PhoneText,
  TotalSpentText,
  TransactionHistoryText,
  DateTimeText,
  TransactionAmountText,
} from './styles';

const CustomerTable: React.FC<CustomerTableProps> = ({
  customerLoyalties,
  totalPages,
  currentPage,
  limit,
  totalCount,
  onPaginationChange,
  loading = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useSelector((state: RootState) => state.theme);

  const theme: DefaultTheme = {
    mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark',
  };

  const getPhoneBlurred = (phone: string): string => {
    if (!phone) return 'N/A';
    if (phone.length > PHONE_MASK_LENGTH) {
      return '*'.repeat(phone.length - PHONE_MASK_LENGTH) + phone.slice(-PHONE_MASK_LENGTH);
    }
    return '*'.repeat(phone.length - 2) + phone.slice(-2);
  };

  const formatDateAndTime = (isoString: string) => {
    const date = new Date(isoString);
    const formattedDate = date.toLocaleDateString('en-GB');
    const formattedTime = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return { formattedDate, formattedTime };
  };


  const handleToggleCollapse = (id: string | number) => {
    dispatch(toggleCustomerCollapse(id));
  };

  const columns = [
    {
      title: (
        <ColumnTitleWrapper>
          <FontAwesomeIcon icon={faUsers} style={{ color: '#3b82f6' }} />
          <span>Name</span>
        </ColumnTitleWrapper>
      ),
      dataIndex: 'user',
      key: 'name',
      width: '25%',
      sorter: (a: CustomerLoyalty, b: CustomerLoyalty) => {
        const aValue = a.user.name.toLowerCase();
        const bValue = b.user.name.toLowerCase();
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      },
      render: (user: CustomerLoyalty['user']) => (
        <TableRow>
          <IconBadge
            $bgColor={isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)'}
            $color="#3b82f6"
          >
            <FontAwesomeIcon icon={faUsers} style={{ fontSize: '16px' }} />
          </IconBadge>
          <UserNameText $isDarkMode={isDarkMode}>
            {user.name}
          </UserNameText>
        </TableRow>
      ),
    },
    {
      title: (
        <ColumnTitleWrapper>
          <FontAwesomeIcon icon={faPhoneAlt} />
          <span>Phone</span>
        </ColumnTitleWrapper>
      ),
      dataIndex: 'user',
      key: 'phone',
      width: '20%',
      sorter: (a: CustomerLoyalty, b: CustomerLoyalty) => {
        const aValue = a.user.phone;
        const bValue = b.user.phone;
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      },
      render: (user: CustomerLoyalty['user']) => (
        <TableRow>
          <IconBadge
            $bgColor={isDarkMode ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)'}
            $color="#22c55e"
          >
            <FontAwesomeIcon icon={faPhoneAlt} size="sm" style={{ fontSize: '16px' }} />
          </IconBadge>
          <PhoneText $isDarkMode={isDarkMode}>
            {getPhoneBlurred(user.phone)}
          </PhoneText>
        </TableRow>
      ),
    },
    {
      title: (
        <ColumnTitleWrapper>
          <FontAwesomeIcon icon={faCoins} />
          <span>Points</span>
        </ColumnTitleWrapper>
      ),
      dataIndex: 'points',
      key: 'points',
      width: '15%',
      sorter: (a: CustomerLoyalty, b: CustomerLoyalty) => {
        const aValue = parseFloat(String(a.points));
        const bValue = parseFloat(String(b.points));
        return aValue - bValue;
      },
      render: (points: string | number) => (
        <TableRow>
          <IconBadge
            $bgColor={isDarkMode ? 'rgba(249, 115, 22, 0.1)' : 'rgba(249, 115, 22, 0.05)'}
            $color="#f97316"
          >
            <FontAwesomeIcon icon={faCoins} style={{ fontSize: '16px' }} />
          </IconBadge>
          <Tag color="orange">{parseFloat(String(points)).toFixed(2)}</Tag>
        </TableRow>
      ),
    },
    {
      title: (
        <ColumnTitleWrapper>
          <FontAwesomeIcon icon={faChartLine} />
          <span>Total Spent</span>
        </ColumnTitleWrapper>
      ),
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      width: '20%',
      sorter: (a: CustomerLoyalty, b: CustomerLoyalty) => {
        const aValue = parseFloat(String(a.totalSpent));
        const bValue = parseFloat(String(b.totalSpent));
        return aValue - bValue;
      },
      render: (totalSpent: string | number) => (
        <TableRow>
          <IconBadge
            $bgColor={isDarkMode ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.05)'}
            $color="#a855f7"
          >
            <FontAwesomeIcon icon={faChartLine} style={{ fontSize: '16px' }} />
          </IconBadge>
          <TotalSpentText $isDarkMode={isDarkMode}>
            ${totalSpent}
          </TotalSpentText>
        </TableRow>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_: any, record: CustomerLoyalty) => (
        <Button
          type="link"
          onClick={() => handleToggleCollapse(record.id)}
          icon={record.isOpen ? <UpOutlined /> : <DownOutlined />}
        >
          {record.isOpen ? 'Hide Details' : 'View Details'}
        </Button>
      ),
    },
  ];

  const expandedRowRender = (record: CustomerLoyalty) => {
    const transactions = record.loyaltyPoints?.[0]?.transactions || [];

    const transactionColumns = [
      {
        title: 'Points Earned',
        dataIndex: 'pointsEarned',
        key: 'pointsEarned',
        render: (points: number) => (
          <Tag color="green" style={{ fontSize: 12 }}>
            +{points}
          </Tag>
        ),
      },
      {
        title: 'Points Redeemed',
        dataIndex: 'pointsRedeemed',
        key: 'pointsRedeemed',
        render: (points: number) => (
          <Tag color="red" style={{ fontSize: 12 }}>
            -{points}
          </Tag>
        ),
      },
      {
        title: 'Transaction Amount',
        dataIndex: 'transactionAmount',
        key: 'transactionAmount',
        render: (amount: number) => (
          <TransactionAmountText $isDarkMode={isDarkMode}>
            ${amount}
          </TransactionAmountText>
        ),
      },
      {
        title: 'Date & Time',
        dataIndex: 'date',
        key: 'date',
        render: (date: string) => {
          const { formattedDate, formattedTime } = formatDateAndTime(date);
          return (
            <DateTimeText $isDarkMode={isDarkMode}>
              {formattedDate} {formattedTime}
            </DateTimeText>
          );
        },
      },
    ];

    return (
      <TransactionCard>
        <TransactionHeader>
          <IconBadge
            $bgColor={isDarkMode ? 'rgba(20, 184, 166, 0.1)' : 'rgba(20, 184, 166, 0.05)'}
            $color="#14b8a6"
          >
            <FontAwesomeIcon icon={faGift} style={{ fontSize: '18px' }} />
          </IconBadge>
          <TransactionHistoryText $isDarkMode={isDarkMode}>
            Transaction History
          </TransactionHistoryText>
          <Badge count={transactions.length} style={{ backgroundColor: '#14b8a6' }} />
        </TransactionHeader>
        <TransactionTable
          columns={transactionColumns}
          dataSource={transactions}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </TransactionCard>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <StyledTable
        columns={columns}
        dataSource={customerLoyalties}
        rowKey="id"
        loading={loading}
        expandable={{
          expandedRowRender,
          expandedRowKeys: customerLoyalties.filter((l) => l.isOpen).map((l) => String(l.id)),
          onExpand: (expanded, record: CustomerLoyalty) => {
            if (expanded) {
              handleToggleCollapse(record.id);
            }
          },
        }}
        pagination={false}
      />
      <TablePagination
        totalCount={totalCount}
        currentPage={currentPage}
        pageSize={limit}
        onChange={onPaginationChange}
        totalLabel="Total customers"
        pageSizeOptions={[...LOYALTY_CONSTANTS.LIMIT_OPTIONS]}
        defaultPageSize={LOYALTY_CONSTANTS.DEFAULT_LIMIT}
        padding="16px 0 0 0"
      />
    </ThemeProvider>
  );
};

export default CustomerTable;
